import { common, crypto, IOHelper, JSONHelper, } from '@neo-one/client-common';
import { BaseState } from '@neo-one/client-full-common';
import { makeErrorWithCode } from '@neo-one/utils';
import { assertAssetType, AssetType, toJSONAssetType } from './AssetType';
import { createSerializeWire, } from './Serializable';
import { BinaryReader, utils } from './utils';
export const InvalidAssetError = makeErrorWithCode('INVALID_ASSET', (message) => message);
const NAME_MAX_LENGTH = 1024;
const PRECISION_MAX = 8;
export class Asset extends BaseState {
    constructor({ version, hash, type, name, amount, available = utils.ZERO, precision, feeMode = 0, fee = utils.ZERO, feeAddress = common.ZERO_UINT160, owner, admin, issuer, expiration, isFrozen = false, }) {
        super({ version });
        this.equals = utils.equals(Asset, this, (other) => common.uInt256Equal(this.hash, other.hash));
        this.toKeyString = utils.toKeyString(Asset, () => this.hashHex);
        this.serializeWire = createSerializeWire(this.serializeWireBase.bind(this));
        verifyAsset({ name, type, amount, precision });
        this.hash = hash;
        this.hashHex = common.uInt256ToHex(hash);
        this.type = type;
        this.name = name;
        this.amount = amount;
        this.available = available;
        this.precision = precision;
        this.feeMode = feeMode;
        this.fee = fee;
        this.feeAddress = feeAddress;
        this.owner = owner;
        this.admin = admin;
        this.issuer = issuer;
        this.expiration = expiration;
        this.isFrozen = isFrozen;
        this.sizeInternal = utils.lazy(() => IOHelper.sizeOfUInt8 +
            IOHelper.sizeOfUInt256 +
            IOHelper.sizeOfUInt8 +
            IOHelper.sizeOfVarString(this.name) +
            IOHelper.sizeOfFixed8 +
            IOHelper.sizeOfFixed8 +
            IOHelper.sizeOfUInt8 +
            IOHelper.sizeOfUInt8 +
            IOHelper.sizeOfFixed8 +
            IOHelper.sizeOfUInt160 +
            IOHelper.sizeOfECPoint(this.owner) +
            IOHelper.sizeOfUInt160 +
            IOHelper.sizeOfUInt160 +
            IOHelper.sizeOfUInt32LE +
            IOHelper.sizeOfBoolean);
    }
    static deserializeWireBase({ reader }) {
        const version = reader.readUInt8();
        const hash = reader.readUInt256();
        const type = assertAssetType(reader.readUInt8());
        const name = reader.readVarString();
        const amount = reader.readFixed8();
        const available = reader.readFixed8();
        const precision = reader.readUInt8();
        reader.readUInt8();
        const fee = reader.readFixed8();
        const feeAddress = reader.readUInt160();
        const owner = reader.readECPoint();
        const admin = reader.readUInt160();
        const issuer = reader.readUInt160();
        const expiration = reader.readUInt32LE();
        const isFrozen = reader.readBoolean();
        return new Asset({
            version,
            hash,
            type,
            name,
            amount,
            available,
            precision,
            fee,
            feeAddress,
            owner,
            admin,
            issuer,
            expiration,
            isFrozen,
        });
    }
    static deserializeWire(options) {
        return this.deserializeWireBase({
            context: options.context,
            reader: new BinaryReader(options.buffer),
        });
    }
    get size() {
        return this.sizeInternal();
    }
    update({ available = this.available, expiration = this.expiration, isFrozen = this.isFrozen, }) {
        return new Asset({
            hash: this.hash,
            type: this.type,
            name: this.name,
            amount: this.amount,
            precision: this.precision,
            fee: this.fee,
            feeAddress: this.feeAddress,
            owner: this.owner,
            admin: this.admin,
            issuer: this.issuer,
            available,
            expiration,
            isFrozen,
        });
    }
    serializeWireBase(writer) {
        writer.writeUInt8(this.version);
        writer.writeUInt256(this.hash);
        writer.writeUInt8(this.type);
        writer.writeVarString(this.name);
        writer.writeFixed8(this.amount);
        writer.writeFixed8(this.available);
        writer.writeUInt8(this.precision);
        writer.writeUInt8(this.feeMode);
        writer.writeFixed8(this.fee);
        writer.writeUInt160(this.feeAddress);
        writer.writeECPoint(this.owner);
        writer.writeUInt160(this.admin);
        writer.writeUInt160(this.issuer);
        writer.writeUInt32LE(this.expiration);
        writer.writeBoolean(this.isFrozen);
    }
    serializeJSON(context) {
        let name = this.name;
        try {
            name = JSON.parse(name);
        }
        catch {
        }
        return {
            version: this.version,
            id: JSONHelper.writeUInt256(this.hash),
            type: toJSONAssetType(this.type),
            name,
            amount: JSONHelper.writeFixed8(this.amount),
            available: JSONHelper.writeFixed8(this.available),
            precision: this.precision,
            owner: JSONHelper.writeECPoint(this.owner),
            admin: crypto.scriptHashToAddress({
                addressVersion: context.addressVersion,
                scriptHash: this.admin,
            }),
            issuer: crypto.scriptHashToAddress({
                addressVersion: context.addressVersion,
                scriptHash: this.issuer,
            }),
            expiration: this.expiration,
            frozen: this.isFrozen,
        };
    }
}
export const verifyAsset = ({ name, type, amount, precision, }) => {
    if (type === AssetType.CreditFlag || type === AssetType.DutyFlag) {
        throw new InvalidAssetError(`Asset type cannot be CREDIT_FLAG or DUTY_FLAG, received: ${type}`);
    }
    const nameBuffer = Buffer.from(name, 'utf8');
    if (nameBuffer.length > NAME_MAX_LENGTH) {
        throw new InvalidAssetError(`Name too long. Max: ${NAME_MAX_LENGTH}, Received: ${nameBuffer.length}`);
    }
    if (amount.lte(utils.ZERO) && !amount.eq(common.NEGATIVE_SATOSHI_FIXED8)) {
        throw new InvalidAssetError(`Amount must be greater than 0. (received ${amount})`);
    }
    if (type === AssetType.Invoice && !amount.eq(common.NEGATIVE_SATOSHI_FIXED8)) {
        throw new InvalidAssetError('Invoice assets must have unlimited amount.');
    }
    if (precision > PRECISION_MAX) {
        throw new InvalidAssetError(`Max precision is 8. Received: ${precision}`);
    }
    if (!amount.eq(utils.NEGATIVE_SATOSHI) && !amount.mod(utils.TEN.pow(utils.EIGHT.subn(precision))).eq(utils.ZERO)) {
        throw new InvalidAssetError('Invalid precision for amount.');
    }
};

//# sourceMappingURL=Asset.js.map
